import type { JSX } from 'react';
import { useEffect } from 'react';
import styled from 'styled-components';
import { Box, Link, Text, WistiaLogoNew, useMq, useTheme } from '@wistia/vhs';
import { appHost } from '~/constantDefinitions';
import { trackStructEvent } from '~/utilities/snowplow';
import type { Account, Media } from './types';

const Container = styled.div`
  background-color: ${({ theme }) => theme.color.brandBlue500};
  border-radius: 27px;
  padding: ${({ theme }) => `${theme.spacing.space02} ${theme.spacing.space06}`};
`;

const TRACKING_PARAMS =
  'utm_type=media-page&utm_campaign=media-page&utm_medium=referral&utm_source=wistia';

const LOGIN_URL = `https://${appHost}/auth/wistia_auth_sign_in_pre_auth`;
const ANONYMOUS_RECORD_URL = `https://${appHost}/anonymous/record?${TRACKING_PARAMS}`;

/*
 * Most signup links, like on Storefront, go through VMA pre-auth at a URL like this:
 *
 *     https://${appHost}/auth/wistia_auth_register_pre_auth
 *
 * We're linking directly to the Auth registration page because pre-auth would drop our UTM parameters.
 *
 * The purpose of pre-auth is to let VMA store some VMA-specific state before sending the user
 * through a generic OAuth flow. For example, when you're signing up for a trial or a paid plan,
 * VMA needs to keep track of what trial you're starting or plan you're purchasing after completing
 * registration. In this case, we can skip pre-auth because we want the defaults: free plan, no trial.
 */
const REGISTRATION_BASE_URL = `https://${appHost}/registration/new`;
// : `https://${appHost}/freebies/new`;
const REGISTRATION_URL = `${REGISTRATION_BASE_URL}?${TRACKING_PARAMS}`;

const SNOWPLOW_LABEL_PREFIX = 'growth-media-page';

export const FreeBrandingBanner = ({
  account,
  media,
  isSignedIn,
}: {
  account: Pick<Account, 'numericId'>;
  media: Pick<Media, 'createdByRecord'>;
  isSignedIn: boolean;
}): JSX.Element => {
  const theme = useTheme();
  const mq = useMq();

  useEffect(() => {
    trackStructEvent({
      category: 'banner',
      action: 'view',
      label: `${SNOWPLOW_LABEL_PREFIX}-banner-${media.createdByRecord ? 'record' : 'hosted'}${
        isSignedIn ? '-login' : ''
      }`,
    });
  }, [isSignedIn, media.createdByRecord]);

  let link: { href: string; text: string; snowplowLabel: string } = {
    href: `${REGISTRATION_URL}&about=${account.numericId}`,
    text: `Sign up${mq.isSmAndUp ? ' for free' : ''}`,
    snowplowLabel: `${SNOWPLOW_LABEL_PREFIX}-sign-up`,
  };

  if (isSignedIn) {
    link = {
      href: LOGIN_URL,
      text: `Log in${mq.isSmAndUp ? ' to your account' : ''}`,
      snowplowLabel: `${SNOWPLOW_LABEL_PREFIX}-log-in`,
    };
  } else if (media.createdByRecord) {
    link = {
      href: `${ANONYMOUS_RECORD_URL}&about=${account.numericId}`,
      text: 'Try it out',
      snowplowLabel: `${SNOWPLOW_LABEL_PREFIX}-try-record`,
    };
  } else {
    // Do nothing
  }

  return (
    <Container>
      <Box
        direction="row"
        alignItems="center"
        justifyContent="center"
        gap={theme.spacing.space03}
      >
        <WistiaLogoNew
          iconOnly={true}
          variant="light"
          height={16}
        />
        <Text
          renderAs="span"
          colorOverride="#fff"
          variant="subtitle1"
        >
          {media.createdByRecord ? 'Recorded' : 'Hosted'} with Wistia.{' '}
          <Link
            href={link.href}
            variant="secondary"
            onClick={() => {
              trackStructEvent({
                category: 'link',
                action: 'click',
                label: link.snowplowLabel,
              });
            }}
          >
            {link.text}
          </Link>
        </Text>
      </Box>
    </Container>
  );
};
